import React, { useMemo, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Reusable "Find Duplicates" button + modal for any list page.
 *
 * Props:
 *   module        — one of products | customers | suppliers | leads | payments
 *                   | sales-invoices | purchase-orders | purchase-entries
 *   deletePath    — fn(record) -> URL fragment used for DELETE /api/<deletePath(record)>
 *                   (defaults to `${module}/${record.id}`)
 *   labelFields   — ordered list of field names used to render each row's
 *                   identifying details inside the modal.
 *   onAfterDelete — optional callback so the parent list page can refresh
 *                   its own data after deletions.
 */
export default function FindDuplicatesButton({
  module,
  deletePath,
  labelFields,
  onAfterDelete,
}) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [selected, setSelected] = useState(new Set()); // ids ticked for bulk delete
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  // Build id->record index so we can resolve selected ids back to records.
  const recordById = useMemo(() => {
    const m = new Map();
    for (const g of groups) {
      for (const r of g.records || []) m.set(r.id, r);
    }
    return m;
  }, [groups]);

  const totalRows = useMemo(
    () => groups.reduce((n, g) => n + (g.records?.length || 0), 0),
    [groups],
  );

  if (!isAdmin) return null;

  const resolvePath = (rec) =>
    (deletePath || ((r) => `${module}/${r.id}`))(rec);

  const fetchDuplicates = async () => {
    setOpen(true);
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const res = await api.get(`/admin/duplicates/${module}`);
      setGroups(res.data?.groups || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (g) => {
    const ids = (g.records || []).map((r) => r.id).filter(Boolean);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleAll = () => {
    const allIds = [];
    for (const g of groups) for (const r of g.records || []) if (r.id) allIds.push(r.id);
    setSelected((prev) => {
      if (allIds.every((id) => prev.has(id))) return new Set();
      return new Set(allIds);
    });
  };

  const deleteOne = async (record, displayName) => {
    const id = record.id;
    if (!window.confirm(`Permanently delete:\n${displayName}\n\nThis cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/${resolvePath(record)}`);
      await fetchDuplicates();
      onAfterDelete?.();
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeletingId('');
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Permanently delete ${ids.length} record${ids.length === 1 ? '' : 's'}?\n\nThis cannot be undone.`
    )) return;

    setBulkDeleting(true);
    setBulkProgress({ done: 0, total: ids.length });
    const failures = [];
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      const rec = recordById.get(id);
      if (!rec) { failures.push({ id, msg: 'record missing' }); continue; }
      try {
        await api.delete(`/${resolvePath(rec)}`);
      } catch (e) {
        failures.push({ id, msg: e.response?.data?.detail || e.message });
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkDeleting(false);
    setSelected(new Set());
    await fetchDuplicates();
    onAfterDelete?.();
    if (failures.length) {
      alert(
        `Deleted ${ids.length - failures.length} of ${ids.length}.\n` +
        `Failures:\n${failures.map((f) => `• ${f.id}: ${f.msg}`).join('\n')}`
      );
    }
  };

  const totalSelected = selected.size;
  const allTicked = totalRows > 0 && totalSelected === totalRows;

  return (
    <>
      <button
        type="button"
        onClick={fetchDuplicates}
        className="px-3 py-1.5 border border-amber-400 text-amber-700 hover:bg-amber-50 rounded text-sm inline-flex items-center gap-1"
        data-testid={`find-duplicates-btn-${module}`}
        title="Scan this list for likely duplicate records"
      >
        🔍 Find Duplicates
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[88vh] overflow-hidden flex flex-col" data-testid="duplicates-modal">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">
                🔍 Duplicate Finder — <span className="font-mono text-sm">{module}</span>
              </h3>
              <button
                onClick={() => { setOpen(false); setGroups([]); setError(''); setSelected(new Set()); }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Bulk action bar */}
            {!loading && !error && groups.length > 0 && (
              <div className="px-4 py-2 border-b bg-amber-50 flex items-center gap-3 flex-wrap text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allTicked}
                    onChange={toggleAll}
                    data-testid="duplicates-select-all"
                  />
                  <span>Select all ({totalRows})</span>
                </label>
                <span className="text-gray-500">
                  {totalSelected} selected
                </span>
                <button
                  onClick={deleteSelected}
                  disabled={totalSelected === 0 || bulkDeleting}
                  className="ml-auto px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed font-semibold"
                  data-testid="duplicates-bulk-delete-btn"
                >
                  {bulkDeleting
                    ? `Deleting ${bulkProgress.done}/${bulkProgress.total}…`
                    : `🗑 Delete Selected (${totalSelected})`}
                </button>
              </div>
            )}

            <div className="p-4 overflow-auto">
              {loading && <div className="text-sm text-gray-500">Scanning…</div>}
              {error && (
                <div className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-900">
                  {error}
                </div>
              )}
              {!loading && !error && groups.length === 0 && (
                <div className="p-6 text-center text-emerald-700 bg-emerald-50 border border-emerald-200 rounded">
                  ✅ No duplicates found.
                </div>
              )}
              {!loading && groups.length > 0 && (
                <div className="space-y-3">
                  <div className="text-xs text-gray-600">
                    Found <b>{groups.length}</b> duplicate group{groups.length === 1 ? '' : 's'}. Review carefully before deleting — duplicates may legitimately exist (e.g., same name at different branches).
                  </div>
                  {groups.map((g, gi) => {
                    const ids = (g.records || []).map((r) => r.id).filter(Boolean);
                    const groupAll = ids.length > 0 && ids.every((id) => selected.has(id));
                    return (
                      <div key={`g-${gi}`} className="border rounded">
                        <div className="px-3 py-1.5 bg-amber-50 border-b text-xs flex items-center gap-2 flex-wrap">
                          <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={groupAll}
                              onChange={() => toggleGroup(g)}
                              data-testid={`duplicates-group-select-${gi}`}
                            />
                            <span className="font-semibold">Matched by:</span>
                          </label>
                          <span>{g.matched_by}</span>
                          <span className="text-gray-400">|</span>
                          <span className="font-mono break-all">{g.matched_value}</span>
                          <span className="ml-auto text-amber-700">{g.count} records</span>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="px-2 py-1.5 w-8"> </th>
                              {labelFields.map((f) => (
                                <th key={f} className="px-2 py-1.5 text-left">{f}</th>
                              ))}
                              <th className="px-2 py-1.5 text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.records.map((r) => (
                              <tr
                                key={r.id}
                                className={`border-b hover:bg-gray-50 ${selected.has(r.id) ? 'bg-amber-50/60' : ''}`}
                                data-testid={`duplicate-row-${r.id}`}
                              >
                                <td className="px-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={selected.has(r.id)}
                                    onChange={() => toggleRow(r.id)}
                                    data-testid={`duplicate-select-${r.id}`}
                                  />
                                </td>
                                {labelFields.map((f) => (
                                  <td key={`${r.id}-${f}`} className="px-2 py-1 break-all">
                                    {String(r[f] ?? '—')}
                                  </td>
                                ))}
                                <td className="px-2 py-1 text-right">
                                  <button
                                    onClick={() => deleteOne(
                                      r,
                                      labelFields.map((f) => r[f]).filter(Boolean).join(' · '),
                                    )}
                                    disabled={deletingId === r.id || bulkDeleting}
                                    className="text-red-700 hover:text-red-900 font-semibold disabled:opacity-50"
                                    data-testid={`delete-duplicate-${r.id}`}
                                  >
                                    {deletingId === r.id ? 'Deleting…' : '🗑 Delete'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 flex justify-end">
              <button
                onClick={() => { setOpen(false); setGroups([]); setError(''); setSelected(new Set()); }}
                className="px-3 py-1.5 border rounded hover:bg-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
